import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useInlineEdits } from '@/hooks/useInlineEdits'
import { apiErrorDescription } from '@/services/core/apiError'
import type { InlineEditPatch } from '@/lib/inlineEdit'

export interface InlineSaveRow<TData> {
  id: string
  row: TData
  patch: InlineEditPatch
}

interface InlineSaveOptions<TData> {
  /** Sends every touched row. One request where the API takes a list. */
  save: (rows: InlineSaveRow<TData>[]) => Promise<unknown>
  /**
   * Query keys to refetch once the save lands — every list and page that can
   * show one of these objects. Matched by prefix, as React Query does.
   */
  invalidateKeys: string[]
  /** Title of the toast a failed save raises. */
  errorTitle: string
  getRowId?: (row: TData) => string
}

/**
 * Staged inline edits plus the save behind them: what a list has to hold to
 * offer editing in place. The table gets `edits`/`setEdit`, and `bar` is
 * spread straight onto `InlineEditBar`.
 */
export function useInlineSave<TData>({
  save,
  invalidateKeys,
  errorTitle,
  getRowId,
}: InlineSaveOptions<TData>) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const inline = useInlineEdits<TData>(getRowId)

  const mutation = useMutation({
    mutationFn: async () => {
      const rows = inline.pending
      await save(rows)
      return rows.length
    },
    onSuccess: (count) => {
      inline.reset()
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      toast.success(t('common.saved'), {
        description: t('inlineEdit.savedCount', { count }),
        duration: 3000,
      })
    },
    onError: (error) => {
      toast.error(errorTitle, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  return {
    edits: inline.edits,
    setEdit: inline.setEdit,
    bar: {
      count: inline.count,
      rowCount: inline.rowCount,
      saving: mutation.isPending,
      onCancel: inline.reset,
      onSave: () => mutation.mutate(),
    },
  }
}
